import { useRoute } from "@react-navigation/native";
import "firebase/firestore";
import "firebase/storage";
import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kRoute } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { uploadProfileImage, uploadRetailTaxInfo, useFetchCompanySettings } from "src/redux/AdminSettings/actions";
import { getDefaultBoutiqueProfile, getDefaultLongAddress } from "src/redux/company/defaultData";
import { tRetailSettingsPath } from "src/redux/company/types";
import { setBackButtonRoute } from "src/redux/navigation/thunkActions";
import { tReduxState } from "src/redux/types";
import { aRetailCategories } from "src/util/comboBoxData";
import { tBoutique } from "tmt_api/src/types/types.validator";
import { formatPhoneNumber, formatShortAddress } from "src/util/gFormatting";
import { Spacer } from "../components";
import { handleErrorEvents } from "../redux/errorHandler/actions";
import AdminUserPanel from "./AdminUserPanel";
import { FormComboBoxInput, tValidType, ValidatedTextFormInput } from "./FormInputs";
import { PdImagePicker, tImagePickerValue } from "./forms/picker";
import { PdButton, PdButtonLabel } from "./forms/button";
import { Import_Sm } from "./impComponents";
import UserSettings from "./UserSettings";
import { getDefaultAddress } from "src/util/user";
import { useRetailerAddressesModal } from "src/hooks/modals";
import { useAppDispatch } from "src/redux";
import { openUrlInNewTab } from "src/util/linking";
import { PdStack } from "./layout";
import { useIsMobile } from "src/hooks/ui";

const companyNameSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "retailer" ? AdminSettingReducer.name : getDefaultBoutiqueProfile().name;

const contactPersonSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "retailer"
    ? AdminSettingReducer.contactPerson
    : getDefaultBoutiqueProfile().contactPerson;

const websiteSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "retailer" ? AdminSettingReducer.website : getDefaultBoutiqueProfile().website;

const instagramSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "retailer"
    ? AdminSettingReducer.links.instagram ?? ""
    : getDefaultBoutiqueProfile().links.instagram ?? "";

const facebookSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "retailer"
    ? AdminSettingReducer.links.facebook ?? ""
    : getDefaultBoutiqueProfile().links.facebook ?? "";

const primaryCategorySelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "retailer"
    ? AdminSettingReducer.primaryCategory
    : getDefaultBoutiqueProfile().primaryCategory;

const aboutMeSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "retailer" ? AdminSettingReducer.aboutMe : getDefaultBoutiqueProfile().aboutMe;

const taxIdSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "retailer" ? AdminSettingReducer.taxId : getDefaultBoutiqueProfile().taxId;

const Content: FC<{ retailerSettings: tBoutique & { type: "retailer" } }> = ({ retailerSettings }) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const rAdminSettings = useSelector((state) => state.AdminSettingReducer);
  const rIsAdmin = useSelector((state) => state.UserReducer.profile.accountType === "tmtAdmin");
  const rRetailerAddresses = useSelector((state) =>
    state.AdminSettingReducer.type === "retailer" ? state.AdminSettingReducer.addresses : [getDefaultLongAddress()],
  );
  const rRetailerAddress = useMemo(() => getDefaultAddress(rRetailerAddresses), [rRetailerAddresses]);
  const retailerAddressesModal = useRetailerAddressesModal();
  const [sIsValid, setIsValid] = useState<tValidType>({});

  const handleOnChangeProfileImage = async ({ file }: tImagePickerValue) => {
    await dispatch(uploadProfileImage(rAdminSettings, file));
    dispatch({
      type: "RIBBON",
      message: retailerSettings.avatar
        ? "Successfully uploaded profile image."
        : "Successfully uploaded profile image. Be sure to Save before leaving page.",
    });
  };

  const saveTermsAndConditions = () => {
    dispatch(
      uploadRetailTaxInfo(
        rAdminSettings.type,
        rAdminSettings.type !== "none" && rAdminSettings.type !== "tmtAdmin" && rAdminSettings.id,
      ),
    );
  };

  const onPressTermsAndConditions = async () => {
    try {
      if (!retailerSettings.taxInfo?.url) return;
      openUrlInNewTab(retailerSettings.taxInfo?.url);
    } catch (e) {
      if (e instanceof Error) dispatch(handleErrorEvents(e.message, "error", e));
    }
  };

  const handleOnPressManageAddresses = () => {
    retailerAddressesModal.openAsync({
      retailerId: rIsAdmin && rAdminSettings.type === "retailer" ? rAdminSettings.id : undefined,
    });
  };

  return (
    <ScrollView style={styles.P09_StackScreenDefault}>
      <View style={[styles.P09_PageContainer, styles.P09_ZeroTop]}>
        <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
          <View style={styles.rowColumn}>
            <Text style={styles.gFormHeader}>Public Profile Information</Text>
            <Text>This information will be visible to brands when browsing your retail page</Text>
          </View>
        </View>
        <View style={styles.formControl}>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRetailSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { name: "" } }}
              dispatchPath="settingsPart.name"
              selectorFunction={companyNameSelector}
              controller={sIsValid}
              setController={setIsValid}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Company Name*"
              uniqueName="companyName"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Company Name",
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRetailSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { contactPerson: "" } }}
              dispatchPath="settingsPart.contactPerson"
              selectorFunction={contactPersonSelector}
              controller={sIsValid}
              setController={setIsValid}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Contact Person*"
              uniqueName="contactPerson"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Contact Person",
              }}
            />
          </View>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRetailSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { website: "" } }}
              dispatchPath="settingsPart.website"
              selectorFunction={websiteSelector}
              controller={sIsValid}
              setController={setIsValid}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Website *"
              rules={{
                required: { message: "Required" },
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
              uniqueName="website"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Website",
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRetailSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { links: { instagram: "" } } }}
              dispatchPath="settingsPart.links.instagram"
              selectorFunction={instagramSelector}
              controller={sIsValid}
              setController={setIsValid}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Instagram"
              uniqueName="instagram"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Instagram Link",
              }}
              rules={{
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRetailSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { links: { facebook: "" } } }}
              dispatchPath="settingsPart.links.facebook"
              selectorFunction={facebookSelector}
              controller={sIsValid}
              setController={setIsValid}
              rules={{
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Facebook"
              uniqueName="facebook"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Facebook Link",
              }}
            />
          </View>
          <View style={[styles.P09_Row, styles.P09_ZTop]}>
            <FormComboBoxInput<tRetailSettingsPath>
              titleStyle={styles.P08_01_InputHeader}
              wrapperStyle={styles.gLinkBankFormItemNoMarginZIndexSingle}
              myTitle="Store Type"
              placeholder={"Select Store Type"}
              ComboOptions={aRetailCategories}
              dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { primaryCategory: "Other" } }}
              dispatchPath="settingsPart.primaryCategory"
              selectorFunction={primaryCategorySelector}
            />
          </View>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRetailSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { aboutMe: "" } }}
              dispatchPath="settingsPart.aboutMe"
              selectorFunction={aboutMeSelector}
              controller={sIsValid}
              setController={setIsValid}
              textInputProps={{
                style: styles.gLinkBankInputBox,
                placeholder: "Give a brief bio about your business.",
                multiline: true,
                numberOfLines: 8,
              }}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="About Me"
              uniqueName="aboutMe"
            />
            <Spacer width={33} />
            <View style={styles.P09_ProfileImageContainer}>
              <Text style={styles.P09_FormTitle}>Profile Image*</Text>
              <PdImagePicker
                style={styles.P09_ImageSelectRound}
                mobileStyle={styles.P09_ImageRound}
                webStyle={webStyles.P09_ImageRound}
                source={{ uri: retailerSettings.avatar }}
                onChange={handleOnChangeProfileImage}
              />
            </View>
          </View>
        </View>
      </View>
      <View style={styles.P09_PageContainer}>
        <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
          <View style={styles.rowColumn}>
            <Text style={styles.gFormHeader}>Store Information</Text>
          </View>
        </View>
        <View style={styles.formControl}>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} style={styles.justifyBetween}>
            {!!rRetailerAddress && (
              <View>
                <Text style={styles.shippingPrimaryLabel}>Primary & Default Ship To Address:</Text>
                {!!rRetailerAddress.name && <Text style={styles.shippingSecondaryLabel}>{rRetailerAddress.name}</Text>}
                <Text style={styles.shippingLabel}>{rRetailerAddress.address1}</Text>
                {!!rRetailerAddress.address2 && <Text style={styles.shippingLabel}>{rRetailerAddress.address2}</Text>}
                <Text style={styles.shippingLabel}>{formatShortAddress(rRetailerAddress)}</Text>
                {!!rRetailerAddress.phone && (
                  <Text style={styles.shippingLabel}>{formatPhoneNumber(rRetailerAddress.phone)}</Text>
                )}
              </View>
            )}
            <View>
              <PdButton onPress={handleOnPressManageAddresses}>
                <PdButtonLabel>Manage Addresses</PdButtonLabel>
              </PdButton>
            </View>
          </PdStack>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.alignStart, styles.mt3]}>
            <View style={styles.flex1}>
              <View style={[styles.row, styles.alignCenter]}>
                <Text style={styles.P09_FormTitle}>Resale Certificate Copy</Text>
                <TouchableOpacity onPress={saveTermsAndConditions}>
                  <Import_Sm style={styles.P09_AbsoluteImport} />
                </TouchableOpacity>
              </View>
              <TouchableOpacity onPress={onPressTermsAndConditions}>
                <Text style={[styles.gFormInput, !retailerSettings.taxInfo?.url ? styles.gFormInputPlaceholder : {}]}>
                  {retailerSettings.taxInfo?.name !== undefined && retailerSettings.taxInfo.name !== ""
                    ? retailerSettings.taxInfo.name
                    : "Upload a copy of your Resale Certificate"}
                </Text>
              </TouchableOpacity>
            </View>
            <ValidatedTextFormInput<tRetailSettingsPath>
              wrapperStyle={[styles.wFull, styles.flex1]}
              titleViewStyle={styles.rowNoMargin}
              dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { taxId: "" } }}
              dispatchPath="settingsPart.taxId"
              selectorFunction={taxIdSelector}
              controller={sIsValid}
              setController={setIsValid}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Tax ID #number"
              uniqueName="taxId"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Company Tax ID",
              }}
            />
          </PdStack>
        </View>
      </View>
      {rIsAdmin ? (
        <>
          <AdminUserPanel />
        </>
      ) : (
        <UserSettings isValid={sIsValid} />
      )}
    </ScrollView>
  );
};

type tAdminRetailerSettingsParams = {
  params: { retailerId?: string };
  key: string;
  name: string;
};

const RetailerSettings: FC = () => {
  const route = useRoute<tAdminRetailerSettingsParams>();
  useFetchCompanySettings(route?.params?.retailerId, "boutiques");
  const dispatch = useAppDispatch();
  const isLoading = useSelector((state) => state.LoadingReducer.companyFetching);
  const adminSettings = useSelector((state) => state.AdminSettingReducer);

  useEffect(() => {
    dispatch(setBackButtonRoute(kRoute.P06));
  }, [dispatch]);

  if (
    isLoading ||
    !adminSettings ||
    adminSettings.type === "none" ||
    (adminSettings.type === "brand" && !adminSettings.id)
  ) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  if (adminSettings.type !== "retailer") {
    return null;
  }

  return <Content retailerSettings={adminSettings} />;
};

export default RetailerSettings;

const styles = StyleSheet.create({
  P08_01_InputHeader: {
    ...kFonts.formLegend,
  },
  P09_AbsoluteImport: {
    ...kStyleConst.kImportSmall,
    alignSelf: "center",
    marginLeft: 3 * kStyleConst.kSpacing,
  },
  P09_FormTitle: {
    ...kFonts.formLegend,
  },
  P09_ImageRound: {
    ...kStyleConst.kImageSelect,
    borderRadius: 360,
  },
  P09_ImageSelectRound: {
    ...kStyleConst.kImageSelect,
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    borderRadius: 360,
    flexDirection: "column",
    justifyContent: "center",
    marginTop: kStyleConst.kSpacing * 4,
  },
  P09_PageContainer: {
    backgroundColor: kColors.white,
    flexGrow: 1,
    marginRight: 6 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
  },
  P09_ProfileImageContainer: {
    marginTop: kStyleConst.kSpacing * 2,
    width: kStyleConst.kImageSelect.width,
  },
  P09_Row: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginVertical: kStyleConst.kSpacing * 2,
    minWidth: kStyleConst.kMinWidth,
  },
  P09_StackScreenDefault: {
    backgroundColor: kColors.brandLight,
    flex: 1,
  },
  P09_ZTop: {
    zIndex: 10,
  },
  P09_ZeroTop: {
    marginTop: 0,
  },
  alignCenter: {
    alignItems: "center",
  },
  alignStart: {
    alignItems: "flex-start",
  },
  flex1: {
    flex: 1,
  },
  formControl: {
    marginBottom: 16,
  },
  gFormHeader: {
    ...kFonts.profileHeader,
    marginBottom: kStyleConst.kSpacing * 2,
  },
  gFormInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginTop: 5,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingRight: 40,
    paddingVertical: 10,
  },
  gFormInputPlaceholder: {
    color: kColors.mediumGrey,
  },
  gFormRowNoMarginValidatedSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gHeaderSpacing: {
    marginVertical: 3 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoMarginZIndexSingle: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputBox: {
    borderColor: kColors.brandPink,
    borderRadius: 2,
    borderWidth: 1,
    flex: 1,
    height: kStyleConst.kApplicationNotesBoxHeight,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    paddingLeft: 2 * kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gSpaceBetweenRow: {
    alignItems: "baseline",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  row: {
    flexDirection: "row",
    position: "relative",
    zIndex: 1000,
  },
  rowColumn: {
    flex: 1,
    marginBottom: 16,
    paddingEnd: 16,
  },
  rowNoMargin: {
    alignItems: "center",
    flexDirection: "row",
    marginTop: 0,
    minHeight: 25,
  },
  shippingLabel: {
    lineHeight: 20,
  },
  shippingPrimaryLabel: {
    fontSize: 12,
    lineHeight: 20,
  },
  shippingSecondaryLabel: {
    fontWeight: "500",
    lineHeight: 20,
  },
  wFull: {
    width: "100%",
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  P09_ImageRound: {
    ...kStyleConst.kImageSelect,
    borderRadius: 360,
  },
} as const;
